import type { SanityFooterGroup, SanityFooterLink, SanityFooterSocial, SanityNavigationAdditionalDropdown, SanityNavigationDrawerDropdown, SanityNavigationDrawerLink, SanityNavigationDropdown, SanityNavigationLink, SanityNavigationProductGroup, SanityNavigationQuickLinks, SanityRouteSettings, SanitySeoMetadata, SanitySiteSettingsNavigation } from "@ouster/shared";
import { toCraftLink, toCraftLinkArray, type CraftLink } from "~/features/sanity/link";
import { toCraftAsset, type CraftAsset } from "~/features/sanity/media";
import type { SanityPageBuilderRenderSection } from "~/features/sanity/page-builder/renderer";
import { toCraftRichText } from "~/features/sanity/rich-text/to-html";
import type { Anchor } from "~/features/sub-nav/sub-nav-anchors";
type CraftBlock<TType extends string, TFields extends Record<string, unknown> = Record<string, never>> = TFields & {
  __typename: TType;
  id?: string;
};
type CraftNavLink = CraftBlock<"navLinks_link_BlockType", {
  link: CraftLink[];
}>;
type CraftNavDropdown = CraftBlock<"navLinks_dropdown_BlockType", {
  links: CraftPrimaryNavChild[];
  text?: string;
}>;
type CraftNavQuickLinks = CraftBlock<"navLinks_quickLinks_BlockType", {
  links: CraftNavLink[];
  text?: string;
}>;
type CraftPrimaryNavChild = CraftNavLink | CraftNavDropdown | CraftNavQuickLinks;
type CraftAdditionalNavLink = CraftBlock<"navAdditionalLinks_link_BlockType", {
  link: CraftLink[];
}>;
type CraftAdditionalNavDropdown = CraftBlock<"navAdditionalLinks_dropdown_BlockType", {
  links: CraftAdditionalNavLink[];
  text?: string;
}>;
type CraftDrawerLink = CraftBlock<"navDrawerLinks_link_BlockType", {
  label?: string;
  link: CraftLink[];
  size?: "default" | "small";
}>;
type CraftDrawerDropdown = CraftBlock<"navDrawerLinks_dropdown_BlockType", {
  links: CraftDrawerLink[];
  text?: string;
}>;
type CraftDrawerAdditionalLink = CraftBlock<"navDrawerAdditionalLinks_link_BlockType", {
  link: CraftLink[];
}>;
type CraftFooterAdditionalLink = CraftBlock<"footerAdditionalLinks_link_BlockType", {
  link: CraftLink[];
}>;
type CraftFooterBottomLink = CraftBlock<"footerBottomLinks_link_BlockType", {
  link: CraftLink[];
}>;
type CraftFooterLink = CraftBlock<"footerLinks_link_BlockType", {
  label?: string;
  link: CraftLink[];
}>;
type CraftFooterGroup = CraftBlock<"footerLinks_group_BlockType", {
  links: CraftFooterLink[];
  title?: string;
}>;
type CraftFooterSocial = CraftBlock<"footerSocials_social_BlockType", {
  type?: string;
  url?: string;
}>;
type SanityPrimaryNavItem = NonNullable<SanitySiteSettingsNavigation["navLinks"]>[number];
type SanityPrimaryNavChild = NonNullable<SanityNavigationDropdown["items"]>[number];
type SanityDrawerItem = NonNullable<SanitySiteSettingsNavigation["navDrawerLinks"]>[number];
export type SanityAppLayoutCommon = {
  announcementBarText?: string;
  extraNavLogo?: CraftAsset[];
  extraNavLogoLink?: CraftLink[];
  extraNavLogoTransparent?: CraftAsset[];
  footerAdditionalLinks: CraftFooterAdditionalLink[];
  footerBottomLinks: CraftFooterBottomLink[];
  footerLink?: CraftLink[];
  footerLinks: CraftFooterGroup[];
  footerSocials: CraftFooterSocial[];
  navAdditionalLinks: Array<CraftAdditionalNavLink | CraftAdditionalNavDropdown>;
  navDrawerAdditionalLinks: CraftDrawerAdditionalLink[];
  navDrawerLinks: Array<CraftDrawerLink | CraftDrawerDropdown>;
  navLinks: Array<CraftNavLink | CraftNavDropdown>;
  newsletterText?: string;
  seo?: Array<{
    __typename: "seo_seo_BlockType";
    description?: string;
    image?: Array<{
      src?: string;
    }>;
    title?: string;
  }>;
};
function compact<T>(items: Array<T | undefined>) {
  return items.filter((item): item is T => Boolean(item));
}
function blockId(item: {
  _key?: string;
}, fallback: string) {
  return item._key || fallback;
}
function sameText(a?: string, b?: string) {
  return Boolean(a && b && a.trim().toLowerCase() === b.trim().toLowerCase());
}
function toSeo(seo?: SanitySeoMetadata) {
  if (!seo?.title && !seo?.description && !seo?.image?.asset?.url) {
    return [];
  }
  return [{
    __typename: "seo_seo_BlockType" as const,
    description: seo.description,
    image: seo.image?.asset?.url ? [{
      src: seo.image.asset.url
    }] : [],
    title: seo.title
  }];
}
function toPrimaryNavLink(item: SanityNavigationLink, index: number): CraftNavLink | undefined {
  const link = toCraftLinkArray(item.link, item.label);
  if (!link.length) return undefined;
  return {
    __typename: "navLinks_link_BlockType",
    id: blockId(item, `nav-link-${index}`),
    link
  };
}
function toPrimaryQuickLinks(item: SanityNavigationQuickLinks, index: number): CraftNavQuickLinks | undefined {
  const links = compact((item.links || []).map(toPrimaryNavLink));
  if (!links.length) return undefined;
  return {
    __typename: "navLinks_quickLinks_BlockType",
    id: blockId(item, `nav-quick-links-${index}`),
    links,
    text: item.title
  };
}
function toPrimaryProductGroup(item: SanityNavigationProductGroup, index: number): CraftNavDropdown | undefined {
  const links = compact((item.links || []).map((child, childIndex) => child._type === "navigationQuickLinks" ? toPrimaryQuickLinks(child, childIndex) : toPrimaryNavLink(child, childIndex)));
  if (!links.length) return undefined;
  return {
    __typename: "navLinks_dropdown_BlockType",
    id: blockId(item, `nav-product-group-${index}`),
    links,
    text: item.title
  };
}
function toPrimaryNavChild(item: SanityPrimaryNavChild, index: number): CraftPrimaryNavChild | undefined {
  switch (item._type) {
    case "navigationLink":
      return toPrimaryNavLink(item, index);
    case "navigationProductGroup":
      return toPrimaryProductGroup(item, index);
    case "navigationQuickLinks":
      return toPrimaryQuickLinks(item, index);
    default:
      return undefined;
  }
}
function toPrimaryNavDropdown(item: SanityNavigationDropdown, index: number): CraftNavDropdown | undefined {
  const links = compact((item.items || []).map(toPrimaryNavChild));
  if (!links.length) return undefined;
  return {
    __typename: "navLinks_dropdown_BlockType",
    id: blockId(item, `nav-dropdown-${index}`),
    links,
    text: item.title
  };
}
function toPrimaryNavItem(item: SanityPrimaryNavItem, index: number): CraftNavLink | CraftNavDropdown | undefined {
  switch (item._type) {
    case "navigationLink":
      return toPrimaryNavLink(item, index);
    case "navigationDropdown":
      return toPrimaryNavDropdown(item, index);
    case "navigationQuickLinks":
      {
        const quickLinks = toPrimaryQuickLinks(item, index);
        if (!quickLinks) return undefined;
        return {
          __typename: "navLinks_dropdown_BlockType",
          id: blockId(item, `nav-quick-dropdown-${index}`),
          links: [quickLinks],
          text: item.title
        };
      }
    default:
      return undefined;
  }
}
function toAdditionalNavLink(item: SanityNavigationLink, index: number): CraftAdditionalNavLink | undefined {
  const link = toCraftLinkArray(item.link, item.label);
  if (!link.length) return undefined;
  return {
    __typename: "navAdditionalLinks_link_BlockType",
    id: blockId(item, `nav-additional-link-${index}`),
    link
  };
}
function toAdditionalNavDropdown(item: SanityNavigationAdditionalDropdown, index: number): CraftAdditionalNavDropdown | undefined {
  const links = compact((item.links || []).map(toAdditionalNavLink));
  if (!links.length) return undefined;
  return {
    __typename: "navAdditionalLinks_dropdown_BlockType",
    id: blockId(item, `nav-additional-dropdown-${index}`),
    links,
    text: item.title
  };
}
export function toCraftNavAdditionalLink(item: SanityNavigationLink | SanityNavigationAdditionalDropdown, index: number) {
  return item._type === "navigationAdditionalDropdown" ? toAdditionalNavDropdown(item, index) : toAdditionalNavLink(item, index);
}
function toDrawerLink(item: SanityNavigationDrawerLink, index: number): CraftDrawerLink | undefined {
  const link = toCraftLinkArray(item.link, item.label);
  if (!link.length) return undefined;
  // The drawer renders `label` as a secondary subline under the link text. Suppress it when it just
  // repeats the resolved link text (e.g. the Downloads/Industries/Insights/Support dropdowns, whose
  // children authored the label equal to the link title) while keeping distinct descriptions such as
  // "Ultra-Wide View Lidar Sensor". Mirrors toFooterLink.
  const label = sameText(item.label, link[0]?.text) ? undefined : item.label;
  return {
    __typename: "navDrawerLinks_link_BlockType",
    id: blockId(item, `nav-drawer-link-${index}`),
    label,
    link,
    size: item.size
  };
}
function toDrawerDropdown(item: SanityNavigationDrawerDropdown, index: number): CraftDrawerDropdown | undefined {
  const links = compact((item.links || []).map(toDrawerLink));
  if (!links.length) return undefined;
  return {
    __typename: "navDrawerLinks_dropdown_BlockType",
    id: blockId(item, `nav-drawer-dropdown-${index}`),
    links,
    text: item.title
  };
}
function toDrawerItem(item: SanityDrawerItem, index: number) {
  return item._type === "navigationDrawerDropdown" ? toDrawerDropdown(item, index) : toDrawerLink(item, index);
}
function toDrawerAdditionalLink(item: SanityNavigationLink, index: number): CraftDrawerAdditionalLink | undefined {
  const link = toCraftLinkArray(item.link, item.label);
  if (!link.length) return undefined;
  return {
    __typename: "navDrawerAdditionalLinks_link_BlockType",
    id: blockId(item, `nav-drawer-additional-link-${index}`),
    link
  };
}
function toFooterAdditionalLink(item: SanityNavigationLink, index: number): CraftFooterAdditionalLink | undefined {
  const link = toCraftLinkArray(item.link, item.label);
  if (!link.length) return undefined;
  return {
    __typename: "footerAdditionalLinks_link_BlockType",
    id: blockId(item, `footer-additional-link-${index}`),
    link
  };
}
function toFooterBottomLink(item: SanityNavigationLink, index: number): CraftFooterBottomLink | undefined {
  const link = toCraftLinkArray(item.link, item.label);
  if (!link.length) return undefined;
  return {
    __typename: "footerBottomLinks_link_BlockType",
    id: blockId(item, `footer-bottom-link-${index}`),
    link
  };
}
function toFooterLink(item: SanityFooterLink, index: number): CraftFooterLink | undefined {
  const link = toCraftLinkArray(item.link, item.label);
  if (!link.length) return undefined;
  const label = sameText(item.label, link[0]?.text) ? undefined : item.label;
  return {
    __typename: "footerLinks_link_BlockType",
    id: blockId(item, `footer-link-${index}`),
    label,
    link
  };
}
function toFooterGroup(item: SanityFooterGroup, index: number): CraftFooterGroup | undefined {
  const links = compact((item.links || []).map(toFooterLink));
  if (!links.length) return undefined;
  return {
    __typename: "footerLinks_group_BlockType",
    id: blockId(item, `footer-group-${index}`),
    links,
    title: item.title
  };
}
function toFooterSocial(item: SanityFooterSocial, index: number): CraftFooterSocial | undefined {
  if (!item.url) return undefined;
  return {
    __typename: "footerSocials_social_BlockType",
    id: blockId(item, `footer-social-${index}`),
    type: item.type,
    url: item.url
  };
}
export function adaptSanitySiteSettingsToAppLayoutCommon(settings?: SanitySiteSettingsNavigation | null): SanityAppLayoutCommon {
  return {
    announcementBarText: toCraftRichText(settings?.announcementBarText),
    extraNavLogo: compact([toCraftAsset(settings?.extraNavLogo)]),
    extraNavLogoLink: toCraftLinkArray(settings?.extraNavLogoLink),
    extraNavLogoTransparent: compact([toCraftAsset(settings?.extraNavLogoNegative)]),
    footerAdditionalLinks: compact((settings?.footerAdditionalLinks || []).map(toFooterAdditionalLink)),
    footerBottomLinks: compact((settings?.footerBottomLinks || []).map(toFooterBottomLink)),
    footerLink: toCraftLinkArray(settings?.footerLink),
    footerLinks: compact((settings?.footerLinks || []).map(toFooterGroup)),
    footerSocials: compact((settings?.footerSocials || []).map(toFooterSocial)),
    navAdditionalLinks: compact((settings?.navAdditionalLinks || []).map(toCraftNavAdditionalLink)),
    navDrawerAdditionalLinks: compact((settings?.navDrawerAdditionalLinks || []).map(toDrawerAdditionalLink)),
    navDrawerLinks: compact((settings?.navDrawerLinks || []).map(toDrawerItem)),
    navLinks: compact((settings?.navLinks || []).map(toPrimaryNavItem)),
    newsletterText: settings?.newsletterText,
    seo: toSeo(settings?.seoMetadata)
  };
}

/**
 * Item shape consumed by `<SubNavLinks navLinks={...} />`. Mirrors the legacy
 * `SubNavLink` fragment selection (`subNavLinks_link_BlockType { link: baseLink { ...Link } }`),
 * so the existing Craft-fragment component renders it unchanged.
 */
export type CraftSubNavLink = {
  __typename: "subNavLinks_link_BlockType";
  id?: string;
  link: CraftLink[];
};

/** Build the route-local sub-nav links the `SubNavLinks` component expects from a route's `subNavLinks[]`. */
export function adaptSanitySubNavLinks(links?: SanityNavigationLink[] | null): CraftSubNavLink[] {
  return compact((links || []).map((item, index): CraftSubNavLink | undefined => {
    const link = toCraftLinkArray(item.link, item.label);
    if (!link.length) return undefined;
    return {
      __typename: "subNavLinks_link_BlockType",
      id: blockId(item, `sub-nav-link-${index}`),
      link
    };
  }));
}

/**
 * Derive in-page anchors from loaded page-builder sections, mirroring the
 * removed dedicated hardware/industry routes. The anchor `id` matches the
 * rendered `<section id>` (`sectionId.current || anchorName`); only sections
 * that have both an id and a human-facing name become anchors.
 */
export function adaptSanitySubNavAnchors(sections?: SanityPageBuilderRenderSection[] | null): Anchor[] {
  return compact((sections || []).map((section): Anchor | undefined => {
    const settings = section.sectionSettings;
    const id = settings?.sectionId?.current || settings?.anchorName;
    const name = settings?.anchorName || settings?.sectionTitle;
    if (!id || !name) return undefined;
    return {
      id,
      name
    };
  }));
}
export function adaptSanityRouteSettingsToAppLayoutProps(route?: SanityRouteSettings | null) {
  const supportLink = toCraftLink(route?.supportLink);
  return {
    backButton: route?.backButton,
    customLogo: toCraftAsset(route?.customLogo),
    footerPattern: route?.footerPattern,
    hideFooter: route?.hideFooter,
    isPageHighlighted: route?.isHighlighted,
    navTheme: route?.navTheme || "solid",
    pageMode: route?.pageMode || "default",
    pageNavAdditionalLinks: compact((route?.pageNavAdditionalLinks || []).map(toCraftNavAdditionalLink)),
    pattern: route?.pagePattern || "dotted",
    supportLink,
    supportLinkVariant: route?.supportLinkVariant
  };
}